import React, { useEffect, useState, useRef } from 'react';
import { Img } from 'react-image';
import Loading from '../../components/Loading';
import { useGuildMembers } from '../../hooks/useGuildMembers';
import CounterPost from './CounterPost';
import SeasonSelect from '../Common/SeasonSelect';
import GroupSelect from '../Common/GroupSelect';
import ProfileWithName from '../../components/ProfileWithName';
import CustomDropdown from '../../components/CustomDropdown';
import { getMonsterImgPath } from '../../utils/monster';
import { isOperator, isSiteAdmin } from '../../utils/permission';
import { MemberAdmin, DeletedAccounts, AdminTarget } from './MemberAdmin';
import { IconChart, IconMemo, IconX } from '../../components/Icons';
import BackButton from '../../components/BackButton';
import { loadMonsterMapping } from '../../utils/monsterMapping';
import { useBackHandler } from '../../hooks/useBackHandler';
import { guildImg } from '../../utils/guildImage';
import GradeBadge from '../../components/GradeBadge';



/** 직급 이름표. 게임 등급을 따라 자동으로 맞춰지므로 보여 주기만 한다 */
/** 길드 정원. 게임에서 정해진 값이라 코드에 둔다 */
/**
 * 계산해 둔 승률·방어율·칼소모율. 탭을 다시 열 때 곧바로 그린다.
 * 뒤에서 새로 받아 바꾸므로 값이 낡아 있지는 않다.
 */
const rateCache = new Map<string, { win: any; def: any; usage: any }>();

const GUILD_CAPACITY = 30;

const ROLE_LABEL: Record<string, string> = {
  master: '마스터',
  submaster: '부마스터',
  senior: '선임',
  member: '길드원'
};

const GuildMember: React.FC = () => {
  // useGuildMembers 훅에서 모든 상태와 핸들러를 직접 사용
  const {
    users, outsiders, loading, error, newUserId, setNewUserId, adding, setAdding, addError, setAddError,
    editNick, setEditNick, nickSaving, setNickSaving, deleteLoading, setDeleteLoading,
    joinRequests, joinLoading, setJoinLoading, joinError,
    myRole, setMyRole, mySiteAdmin, setMySiteAdmin, myUuid, setMyUuid, roleEditId, setRoleEditId,
    roleDropdownRef, roleSelectRefs, popupOpen, setPopupOpen,
    fetchUsers, fetchJoinRequests,
    handleApprove, handleReject,
    handleAddUser,
    getRolePriority
  } = useGuildMembers();

  // 각 가입 요청별로 선택된 길드원 UUID를 저장하는 상태
  const [selectedUserForRequest, setSelectedUserForRequest] = useState<{ [requestUserId: string]: string }>({});

  /**
   * **계정 관리 (사이트 운영자 전용).**
   *
   * 아이디가 곧 로그인 열쇠인 사이트라(비밀번호가 없다) 남의 아이디를 보고 바꾸는 일이다.
   * 그래서 운영진(선임까지)이 아니라 사이트 운영자에게만 연다 — 서버도 같은 기준으로 막는다.
   */
  const [manageTarget, setManageTarget] = useState<AdminTarget | null>(null);
  const [deletedOpen, setDeletedOpen] = useState(false);
  const canManageAccounts = isSiteAdmin({ role: myRole, siteAdmin: mySiteAdmin });
  /** 상대 덱 룬 열람을 켜고 끌 수 있나 (애플톡·뭐해?) */
  const [myRuneGranter, setMyRuneGranter] = useState(false);
  /** [관리] 단추를 보일까 — 운영자, 또는 방덱 룬 스위치만 쓰는 사람 */
  const canOpenManage = canManageAccounts || myRuneGranter;

  // 게시글 목록 팝업 관련 상태
  const [postsPopupOpen, setPostsPopupOpen] = useState(false);
  const [selectedUserPosts, setSelectedUserPosts] = useState<any[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState('');
  const [selectedPostDetail, setSelectedPostDetail] = useState<any>(null);
  const [monsterMapping, setMonsterMapping] = useState<Record<string, any>>({});
  const [currentUser, setCurrentUser] = useState<any>(null);

  // 시즌/그룹 선택 관련 상태
  const [postStep, setPostStep] = useState<'season' | 'group' | 'list'>('season');
  const [selectedSeason, setSelectedSeason] = useState<number>(0);
  const [selectedSiegeWar, setSelectedSiegeWar] = useState<string>('all');
  /** 전투기록 목록에서 공격/방어 중 무엇을 볼지 */
  const [recordSide, setRecordSide] = useState<'atk' | 'def'>('atk');
  const [groups, setGroups] = useState<any[]>([]);
  const [seasonStats, setSeasonStats] = useState<{ wins: number; losses: number; winRate: number }>({ wins: 0, losses: 0, winRate: 0 });
  const [groupStats, setGroupStats] = useState<{ [groupName: string]: { wins: number; losses: number } }>({});
  const [memberWinRates, setMemberWinRates] = useState<{ [nickname: string]: { wins: number; losses: number; winRate: number } }>({});
  // 방어 기록(logType 2)에서 그 사람이 방어에 성공한 비율
  const [memberDefenseRates, setMemberDefenseRates] = useState<{ [nickname: string]: { wins: number; losses: number; winRate: number } }>({});
  /**
   * 칼 소모율·출석율.
   * - 출석: 기록이 남은 점령전 / 전체 점령전
   * - 칼 소모: 나간 점령전마다 쓴 몬스터 수 ÷ 30 (3마리씩 열 번이 한 사람 몫)
   */
  const [memberUsageRates, setMemberUsageRates] = useState<{ [nickname: string]: { attended: number; totalSieges: number; attendanceRate: number; monsters: number; quota: number; usageRate: number } }>({});

  // 전투기록 개수. Post 컬렉션 기준인 postCount 는 설탕 길드원에게 항상 0 이라
  // 승률과 같은 소스(SiegeBattle 공격 기록)에서 센다. 팝업이 보여주는 것도 이 기록이다.
  const battleCountOf = (nickname: string) => {
    const s = memberWinRates[nickname];
    return s ? s.wins + s.losses : 0;
  };
  const [postFilter, setPostFilter] = useState<'all' | 'win' | 'lose'>('all');

  const [profileUploading, setProfileUploading] = useState(false);
  /**
   * 길드 공지. 게임의 길드 정보 화면을 열면 SWEX 플러그인이 서버로 올린다
   * (백엔드 /api/siege-battles/own-guild). 아직 안 올라왔으면 빈 문자열이다.
   */
  const [guildNotice, setGuildNotice] = useState('');

  // registered가 false인 유저 목록
  const unregisteredUsers = users.filter(u => (u as any).registered === false);

  // 해당 유저의 게시글 가져오기 (시즌별)
  /**
   * 그 사람의 전투기록. `side` 로 공격/방어를 고른다.
   *
   *   atk — 공격 기록(logType 1). 그 사람이 친 전투
   *   def — 방어 기록(logType 2). 그 사람이 막은 전투. 승패도 방어한 사람 기준이라
   *         승 = 막아냄이다.
   */
  const fetchUserPosts = async (nickname: string, season: number, group: string = 'all', side: 'atk' | 'def' = 'atk') => {
    setPostsLoading(true);
    try {
      const logType = side === 'def' ? 2 : 1;
      const res = await fetch(`/api/siege-battles/as-posts?author=${encodeURIComponent(nickname)}&logType=${logType}`, { credentials: 'include' });
      const body = await res.json();
      const posts = Array.isArray(body) ? body : (body.posts || []);
      let filtered = Array.isArray(posts) ? posts : [];

      // 그룹 필터 적용
      if (group !== 'all') {
        filtered = filtered.filter((post: any) => post.group === group);
      }

      setSelectedUserPosts(filtered);
    } catch {
      setSelectedUserPosts([]);
    } finally {
      setPostsLoading(false);
    }
  };

  // 그룹 목록 가져오기
  const fetchGroups = async (season: number) => {
    try {
      const response = await fetch('/api/siege-battles/groups');
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      }
    } catch (error) {
    }
  };

  // 시즌 전체 통계 가져오기
  const fetchSeasonStats = async (nickname: string, season: number) => {
    try {
      const res = await fetch(`/api/siege-battles/as-posts?author=${encodeURIComponent(nickname)}`, { credentials: 'include' });
      const body = await res.json();
      const allPosts = Array.isArray(body) ? body : (body.posts || []);

      const wins = allPosts.filter((p: any) => p.result === 'win').length;
      const losses = allPosts.filter((p: any) => p.result === 'lose').length;
      const total = wins + losses;
      const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;

      setSeasonStats({ wins, losses, winRate });

      // 그룹별 통계 계산
      const statsPerGroup: { [groupName: string]: { wins: number; losses: number } } = {};
      allPosts.forEach((post: any) => {
        const groupName = post.group || 'all';
        if (!statsPerGroup[groupName]) {
          statsPerGroup[groupName] = { wins: 0, losses: 0 };
        }
        if (post.result === 'win') {
          statsPerGroup[groupName].wins++;
        } else if (post.result === 'lose') {
          statsPerGroup[groupName].losses++;
        }
      });
      setGroupStats(statsPerGroup);
    } catch (error) {
      setSeasonStats({ wins: 0, losses: 0, winRate: 0 });
      setGroupStats({});
    }
  };

  // 모든 길드원의 승률 가져오기 (승률 API 사용)
  const fetchAllMemberWinRates = async () => {
    try {
      const uuids = users.map(u => u.uuid);
      const cacheKey = uuids.join(',');
      const saved = rateCache.get(cacheKey);
      if (saved) {
        setMemberWinRates(saved.win);
        setMemberDefenseRates(saved.def);
        setMemberUsageRates(saved.usage);
      }

      if (uuids.length === 0) {
        setMemberWinRates({});
        return;
      }

      const res = await fetch('/api/siege-battles/win-rates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uuids })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.winRates) {
          // uuid를 nickname으로 매핑
          const stats: { [nickname: string]: { wins: number; losses: number; winRate: number } } = {};
          for (const user of users) {
            const winRateData = data.winRates[user.uuid];
            if (winRateData) {
              stats[user.nickname] = {
                wins: winRateData.wins,
                losses: winRateData.losses,
                winRate: winRateData.winRate
              };
            } else {
              stats[user.nickname] = { wins: 0, losses: 0, winRate: 0 };
            }
          }
          setMemberWinRates(stats);

          // 방어율도 같은 응답에 온다
          const dstats: { [nickname: string]: { wins: number; losses: number; winRate: number } } = {};
          for (const user of users) {
            const d = data.defenseRates?.[user.uuid];
            dstats[user.nickname] = d
              ? { wins: d.wins, losses: d.losses, winRate: d.winRate }
              : { wins: 0, losses: 0, winRate: 0 };
          }
          setMemberDefenseRates(dstats);

          // 칼 소모율·출석율도 같은 응답에 온다
          const ustats: { [nickname: string]: { attended: number; totalSieges: number; attendanceRate: number; monsters: number; quota: number; usageRate: number } } = {};
          for (const user of users) {
            const s = data.usageRates?.[user.uuid];
            ustats[user.nickname] = s
              ? { attended: s.attended, totalSieges: s.totalSieges, attendanceRate: s.attendanceRate, monsters: s.monsters, quota: s.quota, usageRate: s.usageRate }
              : { attended: 0, totalSieges: 0, attendanceRate: 0, monsters: 0, quota: 0, usageRate: 0 };
          }
          setMemberUsageRates(ustats);
          rateCache.set(cacheKey, { win: stats, def: dstats, usage: ustats });
        }
      }
    } catch (error) {
    }
  };

  // 팝업 열기 (시즌 선택 단계로 시작)
  const openPostsPopup = (nickname: string) => {
    setSelectedUserName(nickname);
    setPostsPopupOpen(true);
    setPostStep('season');
    setSelectedSeason(0);
    setSelectedSiegeWar('all');
    setRecordSide('atk');
    setSelectedUserPosts([]);
  };

  /**
   * 프로필 창의 [내 전투기록] — 이 화면의 "그 사람 기록" 창을 그 이름으로 연다(PageContainer 가 보낸다).
   * 탭은 늘 그려 두므로 이 화면은 이미 떠 있다.
   */
  useEffect(() => {
    const on = (e: Event) => {
      const nick = (e as CustomEvent).detail?.nickname;
      if (nick) openPostsPopup(nick);
    };
    window.addEventListener('swpvpdb:open-records', on);
    return () => window.removeEventListener('swpvpdb:open-records', on);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 게시글 상세 열기.
   *
   * **점령전 기록(`siege-…`)은 게시물이 아니다.** `/api/posts/posting` 에 없어서 404 가
   * 오고, 그 본문(`{message: ...}`)을 그대로 상세로 넘기면 attackTeam 없는 객체가 되어
   * 화면이 터졌다 — 클릭하면 팅기던 원인이다. 그런 줄은 목록이 이미 들고 있는 값으로 연다.
   */
  const fetchPostDetail = async (post: any) => {
    if (!currentUser) {
      alert('사용자 정보를 불러올 수 없습니다. 페이지를 새로고침해주세요.');
      return;
    }
    const postId = typeof post === 'string' ? post : post?._id;
    if (!postId) return;

    if (typeof postId === 'string' && postId.startsWith('siege-')) {
      if (typeof post !== 'object') return;
      /**
       * 방어 기록이면 **우리 방덱이 우리 쪽에 와야 한다.**
       *
       * 상세 화면은 왼쪽(공덱 자리)을 우리 쪽으로 그린다. 방어 기록은 우리 덱이
       * `defenseTeam` 에 들어 있어서 그대로 넘기면 상대 쪽에 붙어 버렸다.
       * 상대가 무슨 덱으로 쳤는지는 기록에 없으니 상대 쪽은 비워 표시하지 않는다.
       */
      const isDefenseLog = !post.attackTeam || post.attackTeam.length === 0;
      setSelectedPostDetail(isDefenseLog
        ? { ...post, attackTeam: post.defenseTeam || [], defenseTeam: [], defenseLog: true }
        : post);
      return;
    }

    try {
      const res = await fetch(`/api/posts/posting/${postId}`, { credentials: 'include' });
      if (!res.ok) return;
      setSelectedPostDetail(await res.json());
    } catch (error) {
      setSelectedPostDetail(null);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchJoinRequests();
    (async () => {
      try {
        const res = await fetch('/api/siege-battles/own-guild');
        const data = await res.json();
        if (data?.guild?.notice) setGuildNotice(data.guild.notice);
      } catch {
        // 공지를 못 받아오면 아래 기본 문구를 그대로 둔다
      }
    })();
    (async () => {
      try {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        const data = await res.json();
        if (data.uuid && data.role) {
          setMyUuid(data.uuid);
          setMyRole(data.role);
          setMySiteAdmin(!!data.siteAdmin);
          setMyRuneGranter(!!data.runeGranter);
          const userObj = {
            uuid: data.uuid,
            nickname: data.nickname || '',
            role: data.role || '',
            profileImg: data.profileImg || ''
          };
          setCurrentUser(userObj);
        } else {
        }
      } catch (error) {
      }
    })();

    // 몬스터 매핑 가져오기
    (async () => {
      try {
        const data = await loadMonsterMapping();
        setMonsterMapping(data);
      } catch (error) {
      }
    })();
  }, []);

  // selectedPostDetail 상태 변경 감지
  useEffect(() => {
  }, [selectedPostDetail, currentUser]);

  /**
   * 길드원 목록이 **실제로 바뀔 때만** 승률을 받는다.
   *
   * `users` 는 그릴 때마다 새로 정렬한 배열이라 이것을 지켜보면 매번 "바뀜" 이 된다 — 받은 승률을 넣으면 다시 그려지고,
   * 다시 받고… 끝없이 돌았다. 길드원 탭은 늘 그려 두므로 **사이트를 열어 둔 사람마다** 서버에 승률 집계를 쉬지 않고
   * 보냈고, NAS 가 그 집계 50개를 동시에 돌리느라 스왑에 빠졌다(2026-10-12 실측: 부하 50, 룬 창 4~9초).
   * 그래서 명단의 uuid 목록(글자)을 지켜본다.
   */
  const usersKey = users.map((u) => u.uuid).join(',');
  useEffect(() => {
    if (users.length > 0) {
      fetchAllMemberWinRates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersKey]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(e.target as Node)) {
        setRoleEditId(null);
      }
    }
    if (roleEditId) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [roleEditId]);

  // 뒤로 가기: 열린 창을 닫고, 기록 팝업 안에서는 단계를 한 칸씩 물린다 (각 창의 닫기·뒤로 버튼과 같다)
  useBackHandler(adding, () => {
    setAdding(false);
    setNewUserId('');
    setAddError('');
  });
  useBackHandler(popupOpen, () => setPopupOpen(false));
  useBackHandler(postsPopupOpen, () => {
    setPostsPopupOpen(false);
    setSelectedUserPosts([]);
    setSelectedUserName('');
    setPostStep('season');
    setSelectedSeason(0);
    setSelectedSiegeWar('all');
  });
  // 단계마다 따로 등록한다 — 한 번 앞으로 갈 때마다 방문 기록 칸이 하나씩 쌓여야 뒤로 가기도 한 단계씩 돈다
  useBackHandler(postsPopupOpen && postStep !== 'season', () => {
    setPostStep('season');
    setSelectedSeason(0);
  });
  useBackHandler(postsPopupOpen && postStep === 'list', () => {
    setPostStep('group');
    setSelectedUserPosts([]);
  });
  useBackHandler(!!manageTarget && canOpenManage, () => setManageTarget(null));
  useBackHandler(deletedOpen && canManageAccounts, () => setDeletedOpen(false));
  useBackHandler(!!selectedPostDetail && !!currentUser, () => setSelectedPostDetail(null));

  return (
    <div 
      style={{ 
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255, 255, 255, 0.5) transparent'
      }}
      className="guild-member-page-scroll"
    >

      <div style={{ 
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '40px 20px',
        minHeight: '100vh'
      }}>
      <div style={{
        textAlign: 'center',
        marginBottom: 32,
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: 20,
        padding: '24px 20px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
          <img loading="lazy"
            src={guildImg('설탕')}
            alt="프로필"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              objectFit: 'cover',
              background: '#e3f0ff',
              border: '2px solid #e3f0ff'
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = `/images/default-profile.png`;
            }}
          />
          <h2 style={{
            color: '#1976d2',
            fontWeight: 800,
            fontSize: 32,
            margin: 0,
            textAlign: 'left',
            letterSpacing: -1,
            background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>설탕</h2>
        </div>
        <p style={{
          color: '#666',
          fontSize: 16,
          margin: 0,
          fontWeight: 500,
          whiteSpace: 'pre-wrap'
        }}>{guildNotice || '귀찮더라도 점령전 승률 낮은 방덱 초기화 부탁드립니다!!!'}</p>
      </div>
      
      {/* 통계 카드 */}
      {!loading && !error && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 12,
          marginBottom: 24
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 16,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)'
          }}>
            <img loading="lazy" src={'/images/master.png'} alt="마스터" style={{ width: 40, height: 32, marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 800, color: '#d62424', marginBottom: 4 }}>
              {users.filter(u => u.role === 'master').length}
            </div>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>마스터</div>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 16,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)'
          }}>
            <img loading="lazy" src={'/images/semiMaster.png'} alt="부마스터" style={{ width: 32, height: 32, marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1976d2', marginBottom: 4 }}>
              {users.filter(u => u.role === 'submaster').length}
            </div>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>부마스터</div>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 16,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)'
          }}>
            <img loading="lazy" src={'/images/senior.png'} alt="선임" style={{ width: 32, height: 32, marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 800, color: '#ff9800', marginBottom: 4 }}>
              {users.filter(u => u.role === 'senior').length}
            </div>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>선임</div>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 16,
            padding: '20px 16px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)'
          }}>
                         <img loading="lazy" src={'/images/member.png'} alt="길드원" style={{ width: 32, height: 32, marginBottom: 8 }} />
            <div style={{ fontSize: 20, fontWeight: 800, color: '#888', marginBottom: 4 }}>
              {users.filter(u => u.role === 'member').length}
            </div>
            <div style={{ fontSize: 12, color: '#666', fontWeight: 600 }}>길드원</div>
          </div>
        </div>
      )}
      {/* 추방된 계정 — 사이트 운영자에게만. 되살릴 사람을 여기서 고른다 */}
      {canManageAccounts && !loading && !error && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button
            onClick={() => setDeletedOpen(true)}
            style={{
              border: 'none', borderRadius: 12, padding: '8px 16px',
              background: 'rgba(255,255,255,0.9)', color: '#546e7a', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
            }}
          >추방된 계정</button>
        </div>
      )}
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 20,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            color: '#667eea',
            fontSize: 16,
            fontWeight: 600
          }}>
            <div style={{
              width: 32,
              height: 32,
              border: '4px solid rgba(102, 126, 234, 0.2)',
              borderTopColor: '#667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span>길드원 목록을 불러오는 중...</span>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      ) : error ? (
        <div style={{
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 16,
          padding: '40px 20px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ fontSize: 24, marginBottom: 12 }}><IconX style={{ color: '#d32f2f' }} /></div>
          <div style={{ color: '#d32f2f', fontSize: 18, fontWeight: 600 }}>{error}</div>
        </div>
      ) : (
        <div style={{
          width: '100%',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 20,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
          border: 'none',
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
          position: 'relative'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 16,
            padding: '20px 0',
            borderBottom: 'none',
            letterSpacing: -0.5,
            position: 'relative'
          }}>
            <div style={{ flex: '0 0 80px', textAlign: 'center', paddingLeft: 12 }}>인원</div>
            <div style={{ flex: '1 1 300px', textAlign: 'left', paddingLeft: 60, minWidth: 200 }}>프로필</div>
            <div style={{ flex: '0 0 56px', textAlign: 'center' }}>승리</div>
            <div style={{ flex: '0 0 56px', textAlign: 'center' }}>패배</div>
            <div style={{ flex: '0 0 64px', textAlign: 'center' }}>승률</div>
            <div style={{ flex: '0 0 64px', textAlign: 'center' }}>방어율</div>
            <div style={{ flex: '0 0 72px', textAlign: 'center' }}>칼소모율</div>
            <div style={{ flex: '0 0 64px', textAlign: 'center' }}>출석율</div>
            <div style={{ flex: '0 0 100px', textAlign: 'center' }}>전투기록</div>
            {/* <div style={{ flex: '0 0 140px', textAlign: 'center' }}>마지막 접속</div> */}
            <div style={{ flex: '0 0 120px', textAlign: 'center' }}>직급</div>
            {canOpenManage && <div style={{ flex: '0 0 70px', textAlign: 'center' }}>관리</div>}
          </div>
          {[...users]
            .sort((a, b) => {
              // 1. 직급 우선 정렬
              const aPriority = getRolePriority(a.role);
              const bPriority = getRolePriority(b.role);
              if (aPriority !== bPriority) {
                return aPriority - bPriority; // 낮은 숫자가 높은 직급
              }
              // 2. 같은 직급 내에서는 승률 높은 순
              const aWinRate = memberWinRates[a.nickname]?.winRate || 0;
              const bWinRate = memberWinRates[b.nickname]?.winRate || 0;
              return bWinRate - aWinRate;
            })
            .map((u, index) => {
            const profileUrl = u.profileImg || '/images/default-profile.png';
            let roleLabel = '';
            let badgeColor = '';
            if (u.role === 'master') { roleLabel = '마스터'; badgeColor = '#d62424'; }
            else if (u.role === 'submaster') { roleLabel = '부마스터'; badgeColor = '#1976d2'; }
            else if (u.role === 'senior') { roleLabel = '선임'; badgeColor = '#ff9800'; }
            else { roleLabel = '길드원'; badgeColor = '#888'; }
            const isMe = myUuid === u.uuid;
            return (
              <div key={u.uuid} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '20px 0',
                borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                background: 'rgba(255, 255, 255, 0.8)',
                transition: 'all 0.3s ease',
                gap: 0,
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(25, 118, 210, 0.05)';
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}>
                <div style={{ flex: '0 0 80px', textAlign: 'center', paddingLeft: 12, fontSize: 14, color: '#666', fontWeight: 600 }}>
                  {index + 1}/{GUILD_CAPACITY}
                </div>
                <div style={{ flex: '1 1 300px', display: 'flex', alignItems: 'center', paddingLeft: 20, gap: 12, minWidth: 200 }}>
                  <Img
                    src={profileUrl}
                    unloader={
                      <img loading="lazy"
                        src="/images/default-profile.png"
                        alt="프로필"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 12,
                          objectFit: 'cover',
                          background: '#e3f0ff',
                          border: '2px solid #e3f0ff',
                          flexShrink: 0,
                          transition: 'transform 0.3s ease',
                          verticalAlign: 'middle',
                          display: 'inline-block'
                        }}
                      />
                    }
                    alt="프로필"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      objectFit: 'cover',
                      background: '#e3f0ff',
                      border: '2px solid #e3f0ff',
                      flexShrink: 0,
                      transition: 'transform 0.3s ease',
                      verticalAlign: 'middle',
                      display: 'inline-block'
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                    <div style={{ fontSize: 17, color: '#333', fontWeight: 700, wordBreak: 'break-all' }}>
                      {u.nickname}
                    </div>
                  </div>
                </div>
                {/* 승리 */}
                <div style={{
                  flex: '0 0 56px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {memberWinRates[u.nickname] !== undefined ? (
                    <div style={{
                      background: 'rgba(33, 150, 243, 0.1)',
                      padding: '4px 10px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#2196F3',
                      border: '1px solid rgba(33, 150, 243, 0.3)'
                    }}>
                      {memberWinRates[u.nickname].wins}
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: '#999' }}>-</span>
                  )}
                </div>
                {/* 패배 */}
                <div style={{
                  flex: '0 0 56px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {memberWinRates[u.nickname] !== undefined ? (
                    <div style={{
                      background: 'rgba(244, 67, 54, 0.1)',
                      padding: '4px 10px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#f44336',
                      border: '1px solid rgba(244, 67, 54, 0.3)'
                    }}>
                      {memberWinRates[u.nickname].losses}
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: '#999' }}>-</span>
                  )}
                </div>
                {/* 승률 */}
                <div style={{
                  flex: '0 0 64px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {memberWinRates[u.nickname] !== undefined ? (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      padding: '4px 12px',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 700,
                      color: '#764ba2',
                      border: '1px solid rgba(102, 126, 234, 0.3)'
                    }}>
                      {memberWinRates[u.nickname].winRate}%
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: '#999' }}>-</span>
                  )}
                </div>
                {/* 방어율 — 방어 기록 중 방어에 성공한 비율 */}
                <div style={{
                  flex: '0 0 64px',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {memberDefenseRates[u.nickname] && (memberDefenseRates[u.nickname].wins + memberDefenseRates[u.nickname].losses) > 0 ? (
                    <div
                      title={`방어 성공 ${memberDefenseRates[u.nickname].wins} / 실패 ${memberDefenseRates[u.nickname].losses}`}
                      style={{
                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.12) 0%, rgba(33, 150, 243, 0.06) 100%)',
                        padding: '4px 12px',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#1971c2',
                        border: '1px solid rgba(33, 150, 243, 0.3)'
                      }}
                    >
                      {memberDefenseRates[u.nickname].winRate}%
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: '#999' }}>-</span>
                  )}
                </div>
                {/* 칼 소모율 — 나간 점령전마다 쓴 몬스터 ÷ 30 */}
                <div style={{ flex: '0 0 72px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {memberUsageRates[u.nickname] && memberUsageRates[u.nickname].quota > 0 ? (
                    <div
                      title={`쓴 몬스터 ${memberUsageRates[u.nickname].monsters} / 정원 ${memberUsageRates[u.nickname].quota} (나간 점령전 ${memberUsageRates[u.nickname].attended}회 × 30)`}
                      style={{
                        background: 'rgba(255, 152, 0, 0.12)',
                        padding: '4px 10px',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#e8830c',
                        border: '1px solid rgba(255, 152, 0, 0.35)'
                      }}>
                      {memberUsageRates[u.nickname].usageRate}%
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: '#999' }}>-</span>
                  )}
                </div>
                {/* 출석율 — 기록이 남은 점령전 비율 */}
                <div style={{ flex: '0 0 64px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {memberUsageRates[u.nickname] && memberUsageRates[u.nickname].totalSieges > 0 ? (
                    <div
                      title={`점령전 ${memberUsageRates[u.nickname].totalSieges}회 중 ${memberUsageRates[u.nickname].attended}회 참여`}
                      style={{
                        background: 'rgba(76, 175, 80, 0.12)',
                        padding: '4px 10px',
                        borderRadius: 8,
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#2e7d32',
                        border: '1px solid rgba(76, 175, 80, 0.35)'
                      }}>
                      {memberUsageRates[u.nickname].attendanceRate}%
                    </div>
                  ) : (
                    <span style={{ fontSize: 13, color: '#999' }}>-</span>
                  )}
                </div>
                <div
                  style={{
                    flex: '0 0 100px',
                    textAlign: 'center',
                    fontSize: 15,
                    color: '#667eea',
                    fontWeight: 600,
                    cursor: battleCountOf(u.nickname) > 0 ? 'pointer' : 'default',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    if (battleCountOf(u.nickname) > 0) {
                      openPostsPopup(u.nickname);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (battleCountOf(u.nickname) > 0) {
                      const icon = e.currentTarget.querySelector('span:first-child') as HTMLElement;
                      if (icon) {
                        icon.style.transform = 'scale(1.3)';
                        icon.style.transition = 'transform 0.3s ease';
                      }
                      e.currentTarget.style.color = '#4169E1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const icon = e.currentTarget.querySelector('span:first-child') as HTMLElement;
                    if (icon) {
                      icon.style.transform = 'scale(1)';
                    }
                    e.currentTarget.style.color = '#667eea';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span style={{ fontSize: 16, display: 'inline-block' }}><IconMemo /></span>
                    <span>{battleCountOf(u.nickname)}</span>
                  </div>
                </div>
                {/* <div style={{ flex: '0 0 140px', textAlign: 'center', fontSize: 13, color: '#666', fontWeight: 500 }}>
                  {(u as any).lastLoginAt ? new Date((u as any).lastLoginAt).toLocaleDateString('ko-KR', {
                    month: 'short',
                    day: 'numeric'
                  }) : '미접속'}
                </div> */}
                <div style={{ flex: '0 0 120px', textAlign: 'center', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {/* 직급은 게임 등급을 따라 자동으로 맞춰진다 — 사이트에서 바꾸지 않는다 */}
                  <span style={{
                    fontSize: 15,
                    padding: '6px 14px',
                    borderRadius: 16,
                    fontWeight: 700,
                    color: '#fff',
                    background: u.role === 'master' ? '#d62424' : u.role === 'submaster' ? '#1976d2' : u.role === 'senior' ? '#ff9800' : '#888',
                    boxShadow: '0 1px 4px #0001',
                    userSelect: 'none',
                    minWidth: 78,
                    display: 'inline-block'
                  }}>{ROLE_LABEL[u.role] || u.role}</span>
                </div>
                {/* 계정 관리 — 사이트 운영자에게만 보인다 */}
                {canOpenManage && (
                  <div style={{ flex: '0 0 70px', textAlign: 'center' }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setManageTarget(u as AdminTarget); }}
                      title={`${u.nickname} 계정 관리`}
                      style={{
                        border: '1px solid #d0d7e2', borderRadius: 9, background: '#fff', color: '#546e7a',
                        fontSize: 12.5, fontWeight: 700, padding: '6px 10px', cursor: 'pointer'
                      }}
                    >관리</button>
                  </div>
                )}
              </div>
            );
          })}
          {/* 명단에 없는 계정 — 숨기지 않고 알려 준다 */}
          {outsiders.length > 0 && (
            <div style={{
              margin: '12px 4px 0',
              padding: '10px 14px',
              borderRadius: 10,
              background: 'rgba(255, 152, 0, 0.08)',
              border: '1px solid rgba(255, 152, 0, 0.25)',
              fontSize: 13,
              color: '#8a5a00',
              fontWeight: 600,
              lineHeight: 1.6
            }}>
              게임 명단에 없는 계정 {outsiders.length}명: {outsiders.map(u => u.nickname).join(', ')}
              <div style={{ fontWeight: 500, color: '#a3712a', marginTop: 2 }}>
                게임 닉과 다른 이름으로 가입했거나 길드를 나간 계정입니다. 게임 닉으로 맞추면 자동으로 이어집니다.
              </div>
            </div>
          )}
          {/* 길드원 추가 버튼은 없앴다. 명단은 게임에서 자동으로 동기화되므로
              손으로 넣을 자리가 없다(넣어도 다음 동기화에 덮어써진다). */}
        </div>
      )}
      {/* 길드원 추가 모달 */}
      {adding && (
        <div style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 18,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            padding: '32px 28px 24px 28px',
            minWidth: 400,
            maxWidth: '90vw',
            position: 'relative',
          }}>
            <button
              onClick={() => {
                setAdding(false);
                setNewUserId('');
                setAddError('');
              }}
              style={{
                position: 'absolute',
                right: 18,
                top: 14,
                background: 'none',
                border: 'none',
                fontSize: 28,
                color: '#888',
                cursor: 'pointer',
                fontWeight: 700,
                lineHeight: 1
              }}
              title="닫기"
            >×</button>
            <h3 style={{ color: '#1976d2', fontWeight: 800, fontSize: 24, marginBottom: 24, textAlign: 'center', letterSpacing: -1 }}>새 길드원 추가</h3>
            <input
              type="text"
              placeholder="닉네임 입력"
              value={newUserId}
              onChange={e => setNewUserId(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && newUserId.trim()) {
                  handleAddUser();
                }
              }}
              style={{
                width: '100%',
                padding: '14px 18px',
                fontSize: 16,
                border: '2px solid #e0e0e0',
                borderRadius: 10,
                outline: 'none',
                marginBottom: 12,
                boxSizing: 'border-box',
                transition: 'border-color 0.2s',
                fontFamily: 'inherit'
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = '#1976d2'}
              onBlur={(e) => e.currentTarget.style.borderColor = '#e0e0e0'}
              autoFocus
            />
            {addError && (
              <div style={{ color: '#d32f2f', fontSize: 14, marginBottom: 16, textAlign: 'center', fontWeight: 500 }}>
                {addError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleAddUser}
                disabled={!newUserId.trim()}
                style={{
                  flex: 1,
                  background: newUserId.trim() ? 'linear-gradient(135deg, #1976d2 0%, #764ba2 100%)' : '#ccc',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '14px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: newUserId.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s',
                  boxShadow: newUserId.trim() ? '0 4px 12px rgba(25, 118, 210, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (newUserId.trim()) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(25, 118, 210, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (newUserId.trim()) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(25, 118, 210, 0.3)';
                  }
                }}
              >
                추가
              </button>
              <button
                onClick={() => {
                  setAdding(false);
                  setNewUserId('');
                  setAddError('');
                }}
                style={{
                  flex: 1,
                  background: '#fff',
                  color: '#666',
                  border: '2px solid #e0e0e0',
                  borderRadius: 10,
                  padding: '14px',
                  fontWeight: 700,
                  fontSize: 16,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#999';
                  e.currentTarget.style.color = '#333';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e0e0e0';
                  e.currentTarget.style.color = '#666';
                }}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 가입요청 알림 버튼 (운영자만, 요청 있을 때만) */}
              {isOperator({ role: myRole, siteAdmin: mySiteAdmin }) && joinRequests.length > 0 && (
        <button
          onClick={() => setPopupOpen(true)}
          style={{
            position: 'fixed',
            right: 32,
            bottom: 32,
            zIndex: 1000,
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#fff',
            color: '#d62424',
            border: '3px solid #d62424',
            fontSize: 38,
            fontWeight: 900,
            boxShadow: '0 4px 24px #d6242433',
            cursor: 'pointer',
            animation: 'alert-pop 1s infinite alternate',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="가입 요청 알림"
        >
          !
        </button>
      )}
      {/* 가입요청 팝업 */}
      {popupOpen && (
        <div style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.25)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 18,
            boxShadow: '0 4px 32px #0003',
            padding: '32px 28px 24px 28px',
            minWidth: 340,
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflowY: 'auto',
            position: 'relative',
          }}>
            <button
              onClick={() => setPopupOpen(false)}
              style={{
                position: 'absolute',
                right: 18,
                top: 14,
                background: 'none',
                border: 'none',
                fontSize: 22,
                color: '#888',
                cursor: 'pointer',
                fontWeight: 700,
              }}
              title="닫기"
            >×</button>
            <h3 style={{ color: '#d62424', fontWeight: 800, fontSize: 22, marginBottom: 18, textAlign: 'center', letterSpacing: -1 }}>가입 요청 알림</h3>
            {joinRequests.length === 0 ? (
              <div style={{ color: '#888', textAlign: 'center', fontSize: 16, margin: '32px 0' }}>가입 요청이 없습니다.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {joinRequests.map(r => (
                  <div key={r.userId} style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 12,
                    padding: 16,
                    background: '#fafafa'
                  }}>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#333', marginBottom: 4 }}>
                        가입 신청: <span style={{ color: '#1976d2' }}>{r.userId}</span>
                      </div>
                      <div style={{ fontSize: 13, color: '#666' }}>
                        닉네임: {r.nickname} | 신청일: {new Date(r.createdAt).toLocaleDateString('ko-KR')}
                      </div>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 6, display: 'block' }}>
                        매칭할 길드원 선택:
                      </label>
                      <select
                        value={selectedUserForRequest[r.userId] || ''}
                        onChange={(e) => setSelectedUserForRequest({
                          ...selectedUserForRequest,
                          [r.userId]: e.target.value
                        })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: 14,
                          border: '2px solid #e0e0e0',
                          borderRadius: 8,
                          outline: 'none',
                          background: '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">선택 안함 (새 계정 생성)</option>
                        {unregisteredUsers.map(u => (
                          <option key={u.uuid} value={u.uuid}>
                            {u.nickname}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={async () => {
                          const selectedUuid = selectedUserForRequest[r.userId];
                          if (selectedUuid) {
                            // 기존 유저와 매칭하여 승인
                            const res = await fetch('/api/auth/join-approve-match', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              credentials: 'include',
                              body: JSON.stringify({
                                requestUserId: r.userId,
                                matchUuid: selectedUuid
                              })
                            });
                            const data = await res.json();
                            if (data.success) {
                              fetchUsers();
                              fetchJoinRequests();
                              setSelectedUserForRequest({});
                            } else {
                              alert(data.error || '승인 실패');
                            }
                          } else {
                            // 새 계정 생성
                            handleApprove(r.userId);
                          }
                        }}
                        style={{
                          flex: 1,
                          color: '#fff',
                          background: '#1976d2',
                          border: 'none',
                          borderRadius: 8,
                          padding: '10px',
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                      >
                        승인
                      </button>
                      <button
                        onClick={() => handleReject(r.userId)}
                        style={{
                          flex: 1,
                          color: '#fff',
                          background: '#d62424',
                          border: 'none',
                          borderRadius: 8,
                          padding: '10px',
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: 'pointer',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#c41c1c'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#d62424'}
                      >
                        거절
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* 게시글 목록 팝업 */}
      {postsPopupOpen && (
        <div style={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: 'linear-gradient(to bottom, #ffffff 0%, #f8faff 100%)',
            borderRadius: 20,
            boxShadow: '0 12px 48px rgba(102, 126, 234, 0.3), 0 0 0 1px rgba(102, 126, 234, 0.1)',
            padding: '0',
            minWidth: 900,
            maxWidth: '95vw',
            maxHeight: '85vh',
            position: 'relative',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <button
              onClick={() => {
                setPostsPopupOpen(false);
                setSelectedUserPosts([]);
                setSelectedUserName('');
                setPostStep('season');
                setSelectedSeason(0);
                setSelectedSiegeWar('all');
              }}
              style={{
                position: 'absolute',
                right: 12,
                top: 8,
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                fontSize: 24,
                color: '#888',
                cursor: 'pointer',
                fontWeight: 700,
                lineHeight: 1,
                zIndex: 10,
                borderRadius: '50%',
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}
              title="닫기"
            >×</button>

            {/* 뒤로가기 버튼 - 시즌 선택이 아닐 때만 표시 */}
            {postStep !== 'season' && (
              <BackButton onClick={() => {
                  if (postStep === 'group') {
                    setPostStep('season');
                    setSelectedSeason(0);
                  } else if (postStep === 'list') {
                    setPostStep('group');
                    setSelectedUserPosts([]);
                  }
                }} style={{ position: 'absolute', left: 18, top: 14, zIndex: 10 }} />
            )}

            {/* 헤더 섹션 */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '18px',
              borderRadius: '20px 20px 0 0',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)',
              width: '100%',
              margin: '0',
              boxSizing: 'border-box'
            }}>
              <div style={{
                textAlign: 'center',
                color: '#fff',
                fontSize: '20px',
                fontWeight: 800,
                letterSpacing: '0.5px',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                {selectedUserName}의 전투기록
              </div>
            </div>

            <div style={{ padding: '24px 28px 24px 28px', width: '100%', boxSizing: 'border-box', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

            {/* 그룹별 통계 - 리스트 단계일 때만 표시 */}
            {/* 공격 / 방어 기록 고르기 */}
            {postStep === 'list' && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
                {([['atk', '공격 기록'], ['def', '방어 기록']] as const).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => {
                      if (recordSide === key) return;
                      setRecordSide(key);
                      setPostFilter('all');
                      fetchUserPosts(selectedUserName, selectedSeason, selectedSiegeWar, key);
                    }}
                    style={{
                      padding: '8px 20px',
                      borderRadius: 20,
                      border: 'none',
                      background: recordSide === key ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(102, 126, 234, 0.1)',
                      color: recordSide === key ? '#fff' : '#667eea',
                      fontSize: 14,
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {postStep === 'list' && selectedUserPosts.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
                marginTop: 0,
                gap: 16
              }}>
                {/* 승패 통계 */}
                {(() => {
                  const wins = selectedUserPosts.filter((p: any) => p.result === 'win').length;
                  const losses = selectedUserPosts.filter((p: any) => p.result === 'lose').length;
                  const total = wins + losses;
                  const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;

                  return (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 16,
                      flex: 1
                    }}>
                      {/* 승리 박스 */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.08) 100%)',
                        borderRadius: 10,
                        padding: '8px 16px',
                        border: '2px solid rgba(33, 150, 243, 0.3)',
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        minWidth: 80
                      }}>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#1976d2',
                          letterSpacing: '0.3px'
                        }}>승리</span>
                        <span style={{
                          fontSize: 18,
                          fontWeight: 900,
                          color: '#2196F3',
                          textShadow: '0 1px 3px rgba(33, 150, 243, 0.2)'
                        }}>{wins}</span>
                      </div>

                      {/* 패배 박스 */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.08) 100%)',
                        borderRadius: 10,
                        padding: '8px 16px',
                        border: '2px solid rgba(244, 67, 54, 0.3)',
                        boxShadow: '0 2px 8px rgba(244, 67, 54, 0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        minWidth: 80
                      }}>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#d32f2f',
                          letterSpacing: '0.3px'
                        }}>패배</span>
                        <span style={{
                          fontSize: 18,
                          fontWeight: 900,
                          color: '#f44336',
                          textShadow: '0 1px 3px rgba(244, 67, 54, 0.2)'
                        }}>{losses}</span>
                      </div>

                      {/* 승률 박스 */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%)',
                        borderRadius: 10,
                        padding: '8px 16px',
                        border: '2px solid rgba(76, 175, 80, 0.3)',
                        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                        minWidth: 80
                      }}>
                        <span style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#388e3c',
                          letterSpacing: '0.3px'
                        }}>승률</span>
                        <span style={{
                          fontSize: 18,
                          fontWeight: 900,
                          color: '#4caf50',
                          textShadow: '0 1px 3px rgba(76, 175, 80, 0.2)'
                        }}>{winRate}%</span>
                      </div>
                    </div>
                  );
                })()}

                {/* 구분선 */}
                <div style={{
                  height: 1,
                  background: 'linear-gradient(to right, transparent, rgba(102, 126, 234, 0.3), transparent)',
                  margin: '20px 0'
                }}></div>

                {/* 필터 콤보박스 */}
                <CustomDropdown
                  value={postFilter}
                  onChange={(val) => setPostFilter(val)}
                  options={[
                    { value: 'all', label: '전체' },
                    { value: 'win', label: '승리' },
                    { value: 'lose', label: '패배' }
                  ]}
                  icon={<IconChart />}
                  style={{ minWidth: 160 }}
                />
              </div>
            )}

            {/* 시즌 선택 */}
            {postStep === 'season' && (
              <SeasonSelect
                seasons={[22]}
                onSelect={(season) => {
                  setSelectedSeason(season);
                  fetchGroups(season);
                  fetchSeasonStats(selectedUserName, season);
                  setPostStep('group');
                  setPostFilter('all');
                }}
              />
            )}

            {/* 그룹 선택 */}
            {postStep === 'group' && (
              <>
                {/* 시즌 전체 통계 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: 20,
                  marginTop: 0,
                  gap: 16
                }}>
                  {/* 승리 박스 */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.08) 100%)',
                    borderRadius: 10,
                    padding: '8px 16px',
                    border: '2px solid rgba(33, 150, 243, 0.3)',
                    boxShadow: '0 2px 8px rgba(33, 150, 243, 0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    minWidth: 80
                  }}>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#1976d2',
                      letterSpacing: '0.3px'
                    }}>승리</span>
                    <span style={{
                      fontSize: 18,
                      fontWeight: 900,
                      color: '#2196F3',
                      textShadow: '0 1px 3px rgba(33, 150, 243, 0.2)'
                    }}>{seasonStats.wins}</span>
                  </div>

                  {/* 패배 박스 */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(244, 67, 54, 0.08) 100%)',
                    borderRadius: 10,
                    padding: '8px 16px',
                    border: '2px solid rgba(244, 67, 54, 0.3)',
                    boxShadow: '0 2px 8px rgba(244, 67, 54, 0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    minWidth: 80
                  }}>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#d32f2f',
                      letterSpacing: '0.3px'
                    }}>패배</span>
                    <span style={{
                      fontSize: 18,
                      fontWeight: 900,
                      color: '#f44336',
                      textShadow: '0 1px 3px rgba(244, 67, 54, 0.2)'
                    }}>{seasonStats.losses}</span>
                  </div>

                  {/* 승률 박스 */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.08) 100%)',
                    borderRadius: 10,
                    padding: '8px 16px',
                    border: '2px solid rgba(76, 175, 80, 0.3)',
                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                    minWidth: 80
                  }}>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#388e3c',
                      letterSpacing: '0.3px'
                    }}>승률</span>
                    <span style={{
                      fontSize: 18,
                      fontWeight: 900,
                      color: '#4caf50',
                      textShadow: '0 1px 3px rgba(76, 175, 80, 0.2)'
                    }}>{seasonStats.winRate}%</span>
                  </div>
                </div>

                {/* 구분선 */}
                <div style={{
                  height: 1,
                  background: 'linear-gradient(to right, transparent, rgba(102, 126, 234, 0.3), transparent)',
                  margin: '24px 0'
                }}></div>

                <GroupSelect
                  season={selectedSeason}
                  groups={groups}
                  onSelect={(group) => {
                    setSelectedSiegeWar(group);
                    fetchUserPosts(selectedUserName, selectedSeason, group, recordSide);
                    setPostStep('list');
                    setPostFilter('all');
                  }}
                  onBack={() => {
                    setPostStep('season');
                    setSelectedSeason(0);
                  }}
                  showBackButton={false}
                  groupStats={groupStats}
                />
              </>
            )}

            {/* 게시글 목록 */}
            {postStep === 'list' && (
              <>
                {postsLoading ? (
                  <Loading padding="40px 0" />
                ) : selectedUserPosts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#888', fontSize: 16 }}>
                    전투기록이 없습니다.
                  </div>
                ) : (() => {
                  // 필터링된 게시글
                  const filteredPosts = postFilter === 'all'
                    ? selectedUserPosts
                    : selectedUserPosts.filter((p: any) => p.result === postFilter);

                  if (filteredPosts.length === 0) {
                    return (
                      <div style={{ textAlign: 'center', padding: '40px 0', color: '#888', fontSize: 16 }}>
                        해당 조건의 전투기록이 없습니다.
                      </div>
                    );
                  }

                  return (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.15)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    border: '1px solid rgba(102, 126, 234, 0.2)'
                  }}>
                    {filteredPosts.map((post, idx) => {
                      /**
                       * 방어 기록에는 상대 공덱이 없다(게임이 안 준다) — 그게 이 줄이
                       * 방어 기록이라는 표시다. 방어 기록은 상대 공덱·VS 를 아예 그리지 않는다.
                       */
                      const isDefenseLog = !post.attackTeam || post.attackTeam.length === 0;
                      /**
                       * 방어 기록의 `result` 는 **막아낸 사람 기준**이라 뒤집으면 안 된다.
                       * 공격 기록에서만 방어 쪽이 공격 쪽의 반대가 된다.
                       */
                      const defenseWon = isDefenseLog ? post.result === 'win' : post.result !== 'win';
                      return (
                      <div
                        key={post._id}
                        onClick={() => fetchPostDetail(post)}
                        style={{
                          background: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 255, 0.9)',
                          padding: '20px 20px 12px 20px',
                          borderBottom: idx === filteredPosts.length - 1 ? 'none' : '1px solid rgba(102, 126, 234, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: 60,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = idx % 2 === 0 ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 255, 0.9)';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {/* 순번 */}
                        <div style={{
                          flex: '0 1 80px',
                          minWidth: '50px',
                          maxWidth: '80px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          paddingLeft: 8,
                          paddingRight: 8,
                          borderRight: '1px solid rgba(102, 126, 234, 0.15)',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#667eea'
                        }}>
                          {idx + 1}/{filteredPosts.length}
                        </div>

                        {/* 작성자 */}
                        <div style={{
                          flex: '0 1 180px',
                          minWidth: '80px',
                          maxWidth: '180px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          paddingLeft: 8,
                          paddingRight: 8,
                          borderRight: '1px solid rgba(102, 126, 234, 0.15)',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: '100%',
                            minWidth: 0,
                            overflow: 'hidden'
                          }}>
                            <ProfileWithName
                              profileImg={post.profileImg || ''}
                              nickname={post.nickname || ''}
                              date={post.createdAt}
                              // 전투기록은 초까지 보여준다(`as-posts` 가 source=swex 로 준다).
                              // 같은 분에 여러 번 치는 일이 흔해서 분까지만 보면 구별이 안 된다
                              showSeconds={post.source === 'swex'}
                              color="#333333"
                              size={48}
                              fontSize={14}
                              borderRadius={12}
                            />
                          </div>
                        </div>

                        {/* 공덱과 방덱을 감싸는 컨테이너 (VS 포함) */}
                        <div style={{ flex: '1 1 auto', minWidth: '300px', display: 'flex', alignItems: 'center', position: 'relative', gap: 12, borderRight: '1px solid rgba(102, 126, 234, 0.15)' }}>
                          {/* 방어 기록에는 상대 공덱이 없다 — 공덱 칸도 VS 도 그리지 않는다 */}
                          {!isDefenseLog && (
                            <>
                            {/* 공격팀 */}
                            <div style={{ flex: 1, minWidth: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                              {/* 몬스터들을 감싸는 테두리 박스 */}
                              <div style={{
                                position: 'relative',
                                border: post.result === 'win' ? '2px solid #2196F3' : '2px solid #f44336',
                                borderRadius: 12,
                                padding: '16px 12px 8px 12px',
                                background: post.result === 'win' ? 'rgba(33, 150, 243, 0.05)' : 'rgba(244, 67, 54, 0.05)'
                              }}>
                                {/* 공격팀 결과 라벨 - 테두리 상단에 위치 */}
                                <div style={{
                                  position: 'absolute',
                                  top: -10,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  background: post.result === 'win' ? '#2196F3' : '#f44336',
                                  color: '#fff',
                                  padding: '2px 12px',
                                  borderRadius: 8,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  letterSpacing: '0.5px',
                                  boxShadow: post.result === 'win' ? '0 2px 4px rgba(33, 150, 243, 0.3)' : '0 2px 4px rgba(244, 67, 54, 0.3)'
                                }}>
                                  {post.result === 'win' ? 'WIN' : 'LOSE'}
                                </div>
                                {/* 몬스터들 */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  {post.attackTeam && post.attackTeam.map((m: any, i: number) => {
                                    const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                    return (
                                      <div key={i} style={{ position: 'relative' }}>
                                        <img loading="lazy"
                                          src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                                          alt={displayName}
                                          style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 12,
                                            objectFit: 'contain',
                                            background: post.result === 'win' ? 'linear-gradient(135deg, #fff 0%, #f8faff 100%)' : 'linear-gradient(135deg, #fff 0%, #fff8f8 100%)',
                                            border: post.result === 'win' ? '2px solid rgba(33, 150, 243, 0.2)' : '2px solid rgba(244, 67, 54, 0.2)',
                                            boxShadow: post.result === 'win' ? '0 2px 8px rgba(33, 150, 243, 0.1)' : '0 2px 8px rgba(244, 67, 54, 0.1)'
                                          }}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* VS */}
                            <div style={{
                              flexShrink: 0,
                              padding: '0 6px',
                              color: '#f57c00',
                              fontWeight: 900,
                              fontSize: 20,
                              letterSpacing: '0',
                              textAlign: 'center'
                            }}>
                              VS
                            </div>
                            </>
                          )}

                          {/* 방어팀 */}
                          <div style={{ flex: 1, minWidth: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                            {/* 몬스터들을 감싸는 테두리 박스 */}
                            <div style={{
                              position: 'relative',
                              border: defenseWon ? '2px solid #2196F3' : '2px solid #f44336',
                              borderRadius: 12,
                              padding: '16px 12px 8px 12px',
                              background: defenseWon ? 'rgba(33, 150, 243, 0.05)' : 'rgba(244, 67, 54, 0.05)'
                            }}>
                              {/* 방어팀 결과 라벨 - 테두리 상단에 위치 */}
                              <div style={{
                                position: 'absolute',
                                top: -10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: defenseWon ? '#2196F3' : '#f44336',
                                color: '#fff',
                                padding: '2px 12px',
                                borderRadius: 8,
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                boxShadow: defenseWon ? '0 2px 4px rgba(33, 150, 243, 0.3)' : '0 2px 4px rgba(244, 67, 54, 0.3)'
                              }}>
                                {defenseWon ? 'WIN' : 'LOSE'}
                              </div>
                              {/* 몬스터들 */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {post.defenseTeam && post.defenseTeam.map((m: any, i: number) => {
                                  const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                                  return (
                                    <div key={i} style={{ position: 'relative' }}>
                                      <img loading="lazy"
                                        src={imgPath ? `/mobs/v3/${imgPath.replace(/\\/g, '/')}` : `/images/empty.png`}
                                        alt={displayName}
                                        style={{
                                          width: 48,
                                          height: 48,
                                          borderRadius: 12,
                                          objectFit: 'contain',
                                          background: post.result === 'win' ? 'linear-gradient(135deg, #fff 0%, #fff8f8 100%)' : 'linear-gradient(135deg, #fff 0%, #f8faff 100%)',
                                          border: post.result === 'win' ? '2px solid rgba(244, 67, 54, 0.2)' : '2px solid rgba(33, 150, 243, 0.2)',
                                          boxShadow: post.result === 'win' ? '0 2px 8px rgba(244, 67, 54, 0.1)' : '0 2px 8px rgba(33, 150, 243, 0.1)'
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 상대 길드 */}
                        <div style={{
                          flex: '0 1 180px',
                          minWidth: '80px',
                          maxWidth: '180px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          paddingLeft: 8,
                          paddingRight: 8,
                          overflow: 'hidden'
                        }}>
                          {post.siegeWar ? (
                            <div style={{
                              width: '100%',
                              minWidth: 0,
                              overflow: 'hidden'
                            }}>
                              <ProfileWithName
                                profileImg={guildImg(post.siegeWar)}
                                nickname={post.siegeWar}
                                color="#333333"
                                size={48}
                                fontSize={14}
                                borderRadius={12}
                                reverse={true}
                                alignRight={true}
                                defaultImg="/images/unknown2.png"
                              />
                            </div>
                          ) : (
                            <span style={{ fontSize: 14, color: '#999' }}>-</span>
                          )}
                        </div>
                        {/* 공덱 점수 — 공격 기록만(전투기록 목록과 같다, 사용자 2026-10-26) */}
                        <div style={{ width: 48, minWidth: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <GradeBadge grade={post.simGrade} win={post.simWin} />
                        </div>
                      </div>
                      );
                    })}
                  </div>
                  );
                })()}
              </>
            )}

            </div>
          </div>
        </div>
      )}

      {/*
        길드원 **목록**은 게임 명단 동기화가 맞춘다 — 사람을 손으로 넣고 빼지 않는다.
        여기 있는 것은 **계정**(로그인 아이디·추방) 손보기이고, 사이트 운영자만 쓴다.
      */}
      {manageTarget && canOpenManage && (
        <MemberAdmin
          target={manageTarget}
          onClose={() => setManageTarget(null)}
          onChanged={fetchUsers}
          runeGranter={myRuneGranter}
          accountAdmin={canManageAccounts}
        />
      )}
      {deletedOpen && canManageAccounts && (
        <DeletedAccounts
          onClose={() => setDeletedOpen(false)}
          onChanged={fetchUsers}
        />
      )}
      {/* 게시글 상세보기 팝업 */}
      {selectedPostDetail && currentUser && (
        <CounterPost
          open={!!selectedPostDetail}
          onClose={() => setSelectedPostDetail(null)}
          post={selectedPostDetail}
          monsterMapping={monsterMapping}
          user={currentUser}
          onPostDeleted={() => {
            setSelectedPostDetail(null);
            setPostsPopupOpen(false);
            fetchUsers(); // 게시글 삭제 후 사용자 목록 새로고침
          }}
        />
      )}

      {/* 알림 버튼 애니메이션 */}
      <style>{`
        @keyframes alert-pop {
          0% { box-shadow: 0 4px 24px #d6242400, 0 0 0 0 #d6242422; transform: scale(1); }
          100% { box-shadow: 0 4px 24px #d6242444, 0 0 0 12px #d6242411; transform: scale(1.08); }
        }
      `}</style>
      </div>
    </div>
  );
};

export default GuildMember; 
